import { useState, useEffect } from "react";
import { Sparkles, TrendingUp, Clock, Target, Loader2, ChevronRight, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAIAssistant } from "@/hooks/useAIAssistant";

interface Lead {
  id: string;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  email: string | null;
  lead_score: number | null;
  ai_conversion_score: number | null;
  status: string | null;
  source: string | null;
  last_activity_at: string | null;
}

interface LeadPrioritizationProps {
  leads: Lead[];
  onLeadSelect?: (leadId: string) => void;
}

interface PrioritizedLead extends Lead {
  priority: "hot" | "warm" | "cold";
  reason: string;
}

export function LeadPrioritization({ leads, onLeadSelect }: LeadPrioritizationProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [prioritizedLeads, setPrioritizedLeads] = useState<PrioritizedLead[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const { chat } = useAIAssistant();

  const analyzePriorities = async () => {
    if (!leads || leads.length === 0) return;

    setIsAnalyzing(true);

    // Simple rule-based prioritization that's fast and reliable
    const analyzed: PrioritizedLead[] = leads
      .map((lead) => {
        const score = lead.ai_conversion_score || lead.lead_score || 0;
        const daysSinceActivity = lead.last_activity_at
          ? Math.floor((Date.now() - new Date(lead.last_activity_at).getTime()) / (1000 * 60 * 60 * 24))
          : 999;

        let priority: "hot" | "warm" | "cold";
        let reason: string;

        if (score >= 70 && daysSinceActivity <= 3) {
          priority = "hot";
          reason = `High score (${score}%) with recent activity. Ready to convert.`;
        } else if (score >= 50 || (score >= 30 && daysSinceActivity <= 7)) {
          priority = "warm";
          reason = score >= 50 
            ? `Good score (${score}%). Follow up to maintain momentum.`
            : `Active recently. Nurture to increase engagement.`;
        } else {
          priority = "cold";
          reason = score < 30 
            ? `Low score (${score}%). Needs more nurturing.`
            : `No recent activity. Consider re-engagement campaign.`;
        }

        return { ...lead, priority, reason };
      })
      .sort((a, b) => {
        const priorityOrder = { hot: 0, warm: 1, cold: 2 };
        if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        }
        return (b.ai_conversion_score || b.lead_score || 0) - (a.ai_conversion_score || a.lead_score || 0);
      })
      .slice(0, 5);

    setPrioritizedLeads(analyzed);
    setIsAnalyzing(false);
  };

  useEffect(() => {
    if (leads && leads.length > 0) {
      analyzePriorities();
    }
  }, [leads]);

  const getPriorityConfig = (priority: "hot" | "warm" | "cold") => {
    switch (priority) {
      case "hot":
        return {
          icon: Target,
          label: "Hot",
          className: "bg-destructive/10 text-destructive border-destructive/30",
          iconClass: "text-destructive",
        };
      case "warm":
        return {
          icon: TrendingUp,
          label: "Warm",
          className: "bg-warning/10 text-warning border-warning/30",
          iconClass: "text-warning",
        };
      case "cold":
        return {
          icon: Clock,
          label: "Cold",
          className: "bg-muted text-muted-foreground border-muted",
          iconClass: "text-muted-foreground",
        };
    }
  };

  if (!leads || leads.length === 0) return null;

  return (
    <div className="glass rounded-xl overflow-hidden animate-fade-in">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-muted/20 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-foreground">AI Lead Prioritization</h3>
            <p className="text-xs text-muted-foreground">Focus on your most promising leads</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAnalyzing && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
          <ChevronRight className={cn("h-5 w-5 text-muted-foreground transition-transform", isExpanded && "rotate-90")} />
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-3">
          {prioritizedLeads.length === 0 && !isAnalyzing ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <AlertCircle className="h-4 w-4" />
              <span>No prioritization data available yet</span>
            </div>
          ) : (
            prioritizedLeads.map((lead, index) => {
              const config = getPriorityConfig(lead.priority);
              const PriorityIcon = config.icon;

              return (
                <div
                  key={lead.id}
                  onClick={() => onLeadSelect?.(lead.id)}
                  className="group flex items-start gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors animate-fade-in"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border", config.className)}>
                      <PriorityIcon className="h-3 w-3" />
                      {config.label}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">
                      {lead.first_name} {lead.last_name}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{lead.company_name || lead.email}</p>
                    <p className="text-xs text-primary mt-1">{lead.reason}</p>
                  </div>
                  <div className="flex-shrink-0">
                    <span className="text-sm font-semibold text-foreground">
                      {lead.ai_conversion_score || lead.lead_score || 0}%
                    </span>
                  </div>
                </div>
              );
            })
          )}

          <Button
            variant="outline"
            size="sm"
            className="w-full gap-2"
            onClick={analyzePriorities}
            disabled={isAnalyzing}
          >
            {isAnalyzing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            Refresh Priorities
          </Button>
        </div>
      )}
    </div>
  );
}
